/**
 * sl-y2po.11: Plan-Based Feature Gate Hook + Context
 *
 * Fetches workspace feature availability from the backend and provides
 * it via React Context. Separate from connectivity gates (workflow engine/ELSTER) —
 * this checks if the workspace PLAN includes a feature.
 *
 * Body class bridge: Adds `feature-{key}-allowed` classes to document.body
 * so Lit components can read plan-based feature availability.
 *
 * SOLID:
 * - S: Only plan-based feature availability (not connectivity)
 * - O: New features added via DB seed, no frontend code changes
 */
import { notify } from '@affine/component';
import {
  AuthService,
  DefaultServerService,
  defaultWorkspaceApiClient,
  WorkspaceServerService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { isLocalAuthorityEnvelope } from '@streamlain/view-config';
import { useLiveData, useService } from '@toeverything/infra';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { recordBootEvent } from '../../../bootstrap/boot-trace';
import {
  deleteFeatureEntitlementVault,
  writeFeatureEntitlementVault,
} from '../../local-authority';
import {
  type ExpectedFeatureEntitlementSubject,
  type FeatureEntitlementPayload,
  type FeatureEntitlementStatus,
  hasKnownFeaturePlanConflict,
  type SignedFeatureEntitlement,
} from './feature-entitlement';
import {
  BASE_PLAN_FEATURE_ENTITLEMENT_CACHE_KEY,
  consumeFeatureEntitlement,
  invalidateFeatureEntitlementPreload,
  workspaceFeatureEntitlementCacheKey,
} from './feature-entitlement-preload';
import {
  FEATURE_KEY_COLLECTIONS,
  FEATURE_KEY_TAGS,
  setProductFeatureGateState,
} from './product-feature-gates';

// ============================================================================
// Types
// ============================================================================

export interface FeatureAvailability {
  featureKey: string;
  displayName: string;
  /** Optional display grouping. Backend feature entitlements do not use this as authority. */
  category?: string;
  allowed: boolean;
  /** Whether the integration is actually configured (credentials, connection, etc.) */
  configured: boolean;
  requiredPlan: string | null;
  /** 'hidden' features are invisible when not allowed */
  visibility: 'visible' | 'hidden';
}

export interface FeatureGateContextValue {
  /** All features with their plan-based availability */
  features: FeatureAvailability[];
  /** The resolved workspace plan name (a plan row of the server); null until known */
  plan: string | null;
  /** Check if a specific feature is allowed on the current plan */
  isFeatureAllowed: (featureKey: string) => boolean;
  /** Check if a specific feature's integration is configured */
  isFeatureConfigured: (featureKey: string) => boolean;
  /** Get the required plan for a feature (null if already allowed) */
  getRequiredPlan: (featureKey: string) => string | null;
  /** Show inline toast notification when a locked feature is accessed */
  notifyFeatureLocked: (featureKey: string) => void;
  /** Re-fetch feature availability from the backend. Returns false on fetch failure. */
  refreshFeatures: (options?: FeatureGateRefreshOptions) => Promise<boolean>;
  /** Whether the initial fetch is still loading */
  isLoading: boolean;
  /** Whether the account-scoped signed local entitlement has been checked. */
  isLocalEntitlementReady: boolean;
  /** Validity of the local signed entitlement cache. Online responses remain server-authoritative. */
  entitlementStatus: FeatureEntitlementStatus;
}

export interface FeatureGateRefreshOptions {
  /**
   * Fail closed for sensitive entrypoints. When a refresh fails, callers such
   * as AppBases can clear cached allow-state instead of using stale entitlements.
   */
  failClosed?: boolean;
}

// ============================================================================
// Context
// ============================================================================

const defaultContext: FeatureGateContextValue = {
  features: [],
  plan: null,
  // Pre-provider-mount: deny so locked UI never flashes before the real
  // catalog is wired. Post-mount the real hook returns values driven by
  // the fetched features array.
  isFeatureAllowed: () => false,
  isFeatureConfigured: () => false,
  getRequiredPlan: () => null,
  notifyFeatureLocked: () => {}, // noop before provider mounts
  refreshFeatures: async () => false,
  isLoading: true,
  isLocalEntitlementReady: false,
  entitlementStatus: 'missing',
};

export const FeatureGateContext =
  createContext<FeatureGateContextValue>(defaultContext);

// ============================================================================
// Well-Known Feature Keys (match backend plan_features.feature_key)
// ============================================================================

/** Automation feature key (Settings card — Beta-only) */
export const FEATURE_KEY_WORKFLOW = 'streamlain-workflows';

/** sl-33y3: Auto-provisioning feature key (Pro+ — controls sidebar + provisioning) */
export const FEATURE_KEY_WORKFLOW_AUTO_PROVISION = 'workflow-auto-provision';

/** Workspace Mail — personal inboxes and IMAP/SMTP send/receive (Pro+) */
export const FEATURE_KEY_WORKSPACE_MAIL = 'workspace-mail';

/** Private folders — owner-only personal workspace area used by Apps and Mail. */
export const FEATURE_KEY_PRIVATE_FOLDERS = 'private-folders';

/** Teamspaces (Team Bereiche) — creating one needs Team plan or higher. */
export const FEATURE_KEY_TEAMSPACES = 'teamspaces';

/** Database-backed first-class Calendar app. */
export const FEATURE_KEY_DATABASE_CALENDAR = 'database-calendar';

/**
 * Digital PDF signatures through the workspace's own signing provider (Pro+).
 * The visible signature is part of every plan.
 */
export const FEATURE_KEY_PDF_DIGITAL_SIGNATURE = 'pdf-digital-signature';
export const PDF_DIGITAL_SIGNATURE_FEATURE_KEYS = [
  FEATURE_KEY_PDF_DIGITAL_SIGNATURE,
] as const;

/** Support by email from Business on; the help page shows the contact only with it. */
export const FEATURE_KEY_SUPPORT_EMAIL = 'support-email';

/** Workspace collections. Disabled by plan-feature matrix until re-enabled. */
export { FEATURE_KEY_COLLECTIONS };

/** Workspace tags. Disabled by plan-feature matrix until re-enabled. */
export { FEATURE_KEY_TAGS };

/** Workspace Mail needs both features; mail roots live in private folders. */
export const WORKSPACE_MAIL_FEATURE_KEYS = [
  FEATURE_KEY_WORKSPACE_MAIL,
  FEATURE_KEY_PRIVATE_FOLDERS,
] as const;

export function getWorkspaceMailMissingFeature(
  isFeatureAllowed: FeatureGateContextValue['isFeatureAllowed']
): (typeof WORKSPACE_MAIL_FEATURE_KEYS)[number] | null {
  return (
    WORKSPACE_MAIL_FEATURE_KEYS.find(key => !isFeatureAllowed(key)) ?? null
  );
}

/** StreamLain marketplace feature key (active when workflow engine is provisioned, Pro+) */
export const FEATURE_KEY_WORKFLOW_MARKETPLACE = 'marketplace-browse';

/** ELSTER tax filing feature key */
export const FEATURE_KEY_ELSTER = 'elster';

/** Roles & Access section in workspace settings (Team+ only) */
export const FEATURE_KEY_ROLES_AND_ACCESS = 'roles-and-access';

/** User AI Keys — own API keys + Claude Code OAuth (Internal only) */
export const FEATURE_KEY_USER_AI_KEYS = 'user-ai-keys';

/** Expert Agents — dedicated agent identities and context scopes. */
export const FEATURE_KEY_AI_EXPERT_BOTS = 'ai-expert-bots';

/** Durable Target Mode — Targets from Pro. */
export const FEATURE_KEY_AI_TARGET_MODE = 'ai-target-mode';

/** Intelligent model routing — beta/internal rollout gate. */
export const FEATURE_KEY_AI_AUTO_ROUTING = 'ai-auto-routing';

/** Personal assistant access through the workspace Telegram bot. */
export const FEATURE_KEY_ASSISTANT_TELEGRAM = 'assistant.telegram';

/** Managed AI credits — required for server-key AI/OCR spend. */
export const FEATURE_KEY_MANAGED_AI_CREDITS = 'managed-ai-credits';

// Vertical App feature keys (gate independent mini-apps, not AppBase templates)
export const FEATURE_KEY_VIEW_BLOG = 'view-blog'; // news-portal-vertical
export const FEATURE_KEY_VIEW_PORTAL = 'view-portal'; // portal-landing-vertical

/**
 * AppBase infrastructure rollout gate.
 * AppBases are internal-only until the product surface and billing controls
 * are ready for non-internal plans.
 *
 * Replaces the old single `app-bases` (Team+) key. View-template
 * selection stays UX-only (no plan coupling) per sl-bpu6.10.
 */
export const FEATURE_KEY_APP_BASES_CREATE = 'app_bases.create';
export const FEATURE_KEY_APP_BASES_BRANDED_SUBDOMAIN =
  'app_bases.branded_subdomain';
export const FEATURE_KEY_APP_BASES_CUSTOM_DOMAIN = 'app_bases.custom_domain';

// ============================================================================
// Body Class Constants (Lit Bridge)
// ============================================================================

/** Prefix for body class: `feature-{key}-allowed` */
const FEATURE_CLASS_PREFIX = 'feature-';
const FEATURE_CLASS_SUFFIX = '-allowed';

function featureBodyClass(featureKey: string): string {
  return `${FEATURE_CLASS_PREFIX}${featureKey}${FEATURE_CLASS_SUFFIX}`;
}

/**
 * Check if a feature is allowed by reading body class.
 * For use in Lit components that can't use React hooks.
 */
export function isFeatureAllowedFromBody(featureKey: string): boolean {
  return document.body.classList.contains(featureBodyClass(featureKey));
}

// ============================================================================
// Hook
// ============================================================================

async function writeCachedEntitlement(
  cacheKey: string | null,
  entitlement: unknown
): Promise<void> {
  if (
    !cacheKey ||
    !isLocalAuthorityEnvelope<FeatureEntitlementPayload>(entitlement)
  ) {
    return;
  }
  invalidateFeatureEntitlementPreload(cacheKey);
  await writeFeatureEntitlementVault(cacheKey, entitlement);
}

function isSignedFeatureEntitlement(
  value: unknown
): value is SignedFeatureEntitlement {
  if (!value || typeof value !== 'object') return false;
  const envelope = value as Record<string, unknown>;
  return (
    envelope.version === 1 &&
    envelope.alg === 'ES256' &&
    typeof envelope.kid === 'string' &&
    typeof envelope.signature === 'string' &&
    !!envelope.payload &&
    typeof envelope.payload === 'object'
  );
}

async function clearCachedFeatures(cacheKey: string | null): Promise<void> {
  if (!cacheKey) return;
  invalidateFeatureEntitlementPreload(cacheKey);
  await deleteFeatureEntitlementVault(cacheKey);
}

/**
 * Core hook that fetches feature availability and manages body classes.
 * Used internally by FeatureGateProvider.
 */
export function useFeatureGateFetch(): FeatureGateContextValue {
  const workspace = useService(WorkspaceService).workspace;
  const authService = useService(AuthService);
  const workspaceServerService = useService(WorkspaceServerService);
  const defaultServerService = useService(DefaultServerService);
  const quotaService = useService(WorkspaceQuotaService);
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  const account = useLiveData(authService.session.account$);
  const workspaceServer = useLiveData(workspaceServerService.server$);
  const quotaPlan = useLiveData(quotaService.quota.planName$);
  const billingState = useLiveData(
    workspaceSubscriptionService.subscription.billingState$
  );
  const billingSubscription = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );
  const workspaceId = workspace.id;
  const accountId = account?.id ?? null;
  const isLocal = workspace.flavour === 'local';
  const t = useI18n();
  const workspaceDialogService = useService(WorkspaceDialogService);

  // Local workspaces get the features of the base plan, like a cloud
  // workspace without a plan subscription. Cloud workspaces cache per
  // workspaceId to pick up plan-specific overrides.
  const cacheKey = isLocal
    ? BASE_PLAN_FEATURE_ENTITLEMENT_CACHE_KEY
    : accountId
      ? workspaceFeatureEntitlementCacheKey(workspaceId, accountId)
      : null;
  const cachedBillingState = !isLocal
    ? workspaceSubscriptionService.subscription.store?.getCachedWorkspaceBillingState(
        workspaceId
      )
    : undefined;
  const entitlementPlan =
    quotaPlan ??
    billingState?.effectivePlan ??
    cachedBillingState?.effectivePlan ??
    billingState?.billingSubscription?.plan ??
    cachedBillingState?.billingSubscription?.plan ??
    billingSubscription?.plan ??
    null;
  const expectedEntitlement =
    useMemo<ExpectedFeatureEntitlementSubject | null>(() => {
      if (isLocal) {
        return { subjectType: 'base-plan' };
      }
      if (!accountId) {
        return null;
      }
      return {
        subjectType: 'workspace',
        workspaceId,
        userId: accountId,
        plan: entitlementPlan,
      };
    }, [accountId, entitlementPlan, isLocal, workspaceId]);

  // Signed cache verification is async (WebCrypto), so start locked and hydrate
  // from a valid local entitlement before/alongside the backend refresh.
  const [features, setFeatures] = useState<FeatureAvailability[]>([]);
  const [plan, setPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLocalEntitlementReady, setIsLocalEntitlementReady] = useState(false);
  const [entitlementStatus, setEntitlementStatus] =
    useState<FeatureEntitlementStatus>('missing');
  const fetchSeqRef = useRef(0);
  const failClosedSeqRef = useRef(0);
  const onlineEntitlementRevisionRef = useRef(0);
  const appliedCacheKeyRef = useRef<string | null>(null);

  useEffect(() => {
    quotaService.quota.revalidate();
    workspaceSubscriptionService.subscription.revalidate();
  }, [quotaService, workspaceSubscriptionService]);

  // Fetch features + resolved plan from backend
  const refreshFeatures = useCallback(
    async (options: FeatureGateRefreshOptions = {}): Promise<boolean> => {
      const seq = ++fetchSeqRef.current;
      if (options.failClosed) {
        failClosedSeqRef.current = seq;
      }
      // A fail-closed route refresh must be allowed to clear stale allow-state
      // even if the provider's normal refresh starts after it.
      const canApply = () =>
        seq === fetchSeqRef.current ||
        (options.failClosed === true && seq === failClosedSeqRef.current);
      const url = isLocal
        ? '/api/base-plan/features'
        : `/api/workspace/${workspaceId}/features`;

      try {
        let data: unknown;
        if (isLocal) {
          const res = await defaultServerService.server.fetch(url, {
            credentials: 'include',
          });
          if (!res.ok) {
            const backendUnavailable = isBackendUnavailableError({
              status: res.status,
              message: res.statusText,
            });
            if (options.failClosed && canApply() && !backendUnavailable) {
              setFeatures([]);
              setEntitlementStatus('invalid');
              await clearCachedFeatures(cacheKey);
            }
            return false;
          }
          data = await res.json();
        } else {
          data = await defaultWorkspaceApiClient.requestJson({
            workspaceId,
            baseUrl: workspaceServer?.baseUrl,
            path: url,
            category: 'workspace-api',
            key: `GET:${url}`,
            mode: options.failClosed ? 'foreground' : 'background',
            priority: options.failClosed ? 'foreground' : 'visible',
          });
        }
        const payload = data as Record<string, unknown>;
        const fetchedPlan =
          typeof payload?.plan === 'string' && payload.plan.trim().length > 0
            ? payload.plan.trim()
            : null;
        const fetchedFeatures = Array.isArray(payload?.features)
          ? (payload.features as FeatureAvailability[])
          : null;
        if (
          fetchedPlan === null ||
          fetchedFeatures === null ||
          fetchedFeatures.length === 0
        ) {
          return false;
        }
        if (
          !isLocal &&
          hasKnownFeaturePlanConflict(entitlementPlan, fetchedPlan)
        ) {
          return false;
        }
        if (!canApply()) return false;

        onlineEntitlementRevisionRef.current += 1;
        setFeatures(fetchedFeatures);
        setPlan(fetchedPlan);
        setEntitlementStatus('online');
        appliedCacheKeyRef.current = cacheKey;
        await writeCachedEntitlement(
          cacheKey,
          isSignedFeatureEntitlement(payload.entitlement)
            ? payload.entitlement
            : undefined
        );
        return true;
      } catch (error) {
        if (
          options.failClosed &&
          canApply() &&
          !isBackendUnavailableError(error)
        ) {
          setFeatures([]);
          setEntitlementStatus('invalid');
          appliedCacheKeyRef.current = null;
          await clearCachedFeatures(cacheKey);
        }
        return false;
      } finally {
        if (canApply()) {
          setIsLoading(false);
        }
      }
    },
    [
      cacheKey,
      defaultServerService.server,
      entitlementPlan,
      isLocal,
      workspaceId,
      workspaceServer?.baseUrl,
    ]
  );

  useEffect(() => {
    let cancelled = false;

    const loadLocalEntitlement = async (onlineRevisionAtStart: number) => {
      const startedAt = performance.now();
      if (!cacheKey || !expectedEntitlement) {
        appliedCacheKeyRef.current = null;
        setEntitlementStatus('missing');
        recordBootEvent('feature-entitlement-hydration-finished', {
          durationMs: performance.now() - startedAt,
          outcome: 'missing-subject',
        });
        return;
      }

      const result = await consumeFeatureEntitlement(
        cacheKey,
        expectedEntitlement
      );
      if (
        cancelled ||
        onlineEntitlementRevisionRef.current !== onlineRevisionAtStart
      ) {
        recordBootEvent('feature-entitlement-hydration-finished', {
          durationMs: performance.now() - startedAt,
          outcome: 'superseded',
        });
        return;
      }
      if (result.status === 'valid') {
        setFeatures(result.features);
        setPlan(result.plan);
        appliedCacheKeyRef.current = cacheKey;
      } else {
        setFeatures([]);
        setPlan(null);
        appliedCacheKeyRef.current = null;
      }
      setEntitlementStatus(result.status);
      if (result.status === 'invalid') {
        await clearCachedFeatures(cacheKey);
      }
      recordBootEvent('feature-entitlement-hydration-finished', {
        durationMs: performance.now() - startedAt,
        outcome: result.status,
      });
    };

    const hydrateLocalThenRefresh = async () => {
      // Cache-key changes do not rerun the useState initializer. Verify and
      // apply the signed local entitlement first so local-first UI can render
      // immediately, then fetch current backend truth. Keeping this sequential
      // is intentional: stale local state must never overwrite an online
      // revoke/plan change that arrived faster.
      const sameSubject = appliedCacheKeyRef.current === cacheKey;
      if (!sameSubject) {
        setFeatures([]);
        setPlan(null);
      }
      setIsLoading(true);
      setIsLocalEntitlementReady(false);
      const localStartedAt = performance.now();
      recordBootEvent('feature-entitlement-hydration-started');
      const onlineRevisionAtStart = onlineEntitlementRevisionRef.current;
      try {
        await loadLocalEntitlement(onlineRevisionAtStart);
      } catch {
        if (
          onlineEntitlementRevisionRef.current === onlineRevisionAtStart &&
          !cancelled
        ) {
          setFeatures([]);
          setPlan(null);
          setEntitlementStatus('invalid');
          appliedCacheKeyRef.current = null;
        }
      } finally {
        if (!cancelled) {
          recordBootEvent('feature-entitlement-local-ready', {
            durationMs: performance.now() - localStartedAt,
          });
          setIsLocalEntitlementReady(true);
        }
      }
      if (!cancelled) {
        await refreshFeatures().catch(() => {});
      }
    };

    hydrateLocalThenRefresh().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cacheKey, expectedEntitlement, isLocal, refreshFeatures]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const refreshVisibleWindow = () => {
      refreshFeatures().catch(() => {});
    };
    const refreshVisibleDocument = () => {
      if (document.visibilityState === 'visible') {
        refreshVisibleWindow();
      }
    };

    window.addEventListener('focus', refreshVisibleWindow);
    document.addEventListener('visibilitychange', refreshVisibleDocument);
    return () => {
      window.removeEventListener('focus', refreshVisibleWindow);
      document.removeEventListener('visibilitychange', refreshVisibleDocument);
    };
  }, [refreshFeatures]);

  // Toggle body classes for Lit bridge
  useEffect(() => {
    const allowedClasses: string[] = [];

    for (const f of features) {
      const cls = featureBodyClass(f.featureKey);
      if (f.allowed) {
        document.body.classList.add(cls);
        allowedClasses.push(cls);
      } else {
        document.body.classList.remove(cls);
      }
    }

    // Local workspaces have no billable entitlement subject. Calendar/Planner
    // is nevertheless a local-first product surface, so expose its database
    // view to Lit consumers without turning local feature flags into general
    // entitlement authority. Apply this override after the plan catalog so a
    // missing catalog row never removes the local allowance.
    if (isLocal) {
      const cls = featureBodyClass(FEATURE_KEY_DATABASE_CALENDAR);
      document.body.classList.add(cls);
      allowedClasses.push(cls);
    }

    return () => {
      for (const cls of allowedClasses) {
        document.body.classList.remove(cls);
      }
    };
  }, [features, isLocal]);

  // Memoized lookup helpers
  const allowedSet = useMemo(
    () => new Set(features.filter(f => f.allowed).map(f => f.featureKey)),
    [features]
  );

  const featureMap = useMemo(
    () => new Map(features.map(f => [f.featureKey, f])),
    [features]
  );

  const isFeatureAllowed = useCallback(
    (featureKey: string): boolean => {
      // Empty = no catalog loaded yet (first-ever session pre-fetch) → lock.
      // Backend returns a full catalog even under BILLING_MODE=free (every
      // enabled feature carries allowed:true) so an empty array genuinely
      // means "no data", not "allow everything".
      if (features.length === 0) return false;
      return allowedSet.has(featureKey);
    },
    [features.length, allowedSet]
  );

  const isFeatureConfigured = useCallback(
    (featureKey: string): boolean => {
      if (features.length === 0) return false;
      return featureMap.get(featureKey)?.configured ?? true;
    },
    [features.length, featureMap]
  );

  const getRequiredPlan = useCallback(
    (featureKey: string): string | null => {
      return featureMap.get(featureKey)?.requiredPlan ?? null;
    },
    [featureMap]
  );

  const notifyFeatureLocked = useCallback(
    (featureKey: string): void => {
      const plan = featureMap.get(featureKey)?.requiredPlan;
      if (!plan) {
        if (
          entitlementStatus === 'expired' ||
          entitlementStatus === 'invalid' ||
          entitlementStatus === 'missing'
        ) {
          notify.warning({
            title:
              t[
                'com.streamlain.payment.feature-entitlement-refresh-required'
              ](),
          });
        }
        return;
      }
      notify.warning({
        title: t['com.streamlain.payment.feature-locked.message']({ plan }),
        actions: [
          {
            key: 'upgrade',
            label:
              t['com.streamlain.payment.downgrade-warning.update-payment'](),
            onClick: () =>
              workspaceDialogService.open('setting', {
                activeTab: 'workspace:billing',
              }),
          },
        ],
      });
    },
    [entitlementStatus, featureMap, t, workspaceDialogService]
  );

  useLayoutEffect(() => {
    const isCurrentSubject =
      cacheKey !== null && appliedCacheKeyRef.current === cacheKey;
    setProductFeatureGateState({
      [FEATURE_KEY_COLLECTIONS]:
        isCurrentSubject && isFeatureAllowed(FEATURE_KEY_COLLECTIONS),
      [FEATURE_KEY_TAGS]:
        isCurrentSubject && isFeatureAllowed(FEATURE_KEY_TAGS),
    });
  }, [cacheKey, isFeatureAllowed]);

  return useMemo(
    () => ({
      features,
      plan,
      isFeatureAllowed,
      isFeatureConfigured,
      getRequiredPlan,
      notifyFeatureLocked,
      refreshFeatures,
      isLoading,
      isLocalEntitlementReady,
      entitlementStatus,
    }),
    [
      features,
      plan,
      isFeatureAllowed,
      isFeatureConfigured,
      getRequiredPlan,
      notifyFeatureLocked,
      refreshFeatures,
      isLoading,
      isLocalEntitlementReady,
      entitlementStatus,
    ]
  );
}

// ============================================================================
// Consumer Hook
// ============================================================================

/**
 * Hook to check plan-based feature availability.
 * Must be used within a FeatureGateProvider.
 *
 * @example
 * const { isFeatureAllowed } = useFeatureGate();
 * if (isFeatureAllowed('streamlain-workflows')) { ... }
 */
export function useFeatureGate(): FeatureGateContextValue {
  return useContext(FeatureGateContext);
}
